import { useState, useRef, useEffect } from 'react'
import { Sparkles, Send, X, Loader2, Trash2, Play, CheckCircle, AlertTriangle } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { apiPost } from '@/lib/api'
import Markdown from 'react-markdown'
import { InlineMap } from './InlineMap'
import { InlineRiskMatrix } from './InlineRiskMatrix'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

export function AIChatPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { currentPropertyId } = useAuthStore()
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!input.trim() || streaming || !currentPropertyId) return

    const userMessage = input.trim()
    setInput('')
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }])
    setStreaming(true)

    // Add empty assistant message to stream into
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }])

    try {
      const token = JSON.parse(localStorage.getItem('estatekey-auth') ?? '{}')?.state?.accessToken
      const response = await fetch('/api/v1/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: userMessage,
          propertyId: currentPropertyId,
          conversationHistory: messages.slice(-10), // last 10 for context
        }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'AI service unavailable' }))
        setMessages((prev) => {
          const updated = [...prev]
          updated[updated.length - 1] = {
            role: 'assistant',
            content: `Error: ${err.error ?? 'Something went wrong'}`,
          }
          return updated
        })
        setStreaming(false)
        return
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) throw new Error('No reader')

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const json = line.slice(6)
          try {
            const data = JSON.parse(json)
            if (data.done) break
            if (data.text) {
              setMessages((prev) => {
                const updated = [...prev]
                const last = updated[updated.length - 1]
                updated[updated.length - 1] = {
                  ...last,
                  content: last.content + data.text,
                }
                return updated
              })
            }
          } catch {
            // skip malformed chunks
          }
        }
      }
    } catch (err) {
      setMessages((prev) => {
        const updated = [...prev]
        updated[updated.length - 1] = {
          role: 'assistant',
          content: 'Sorry, I encountered an error connecting to the AI service. Please check that ANTHROPIC_API_KEY is configured.',
        }
        return updated
      })
    } finally {
      setStreaming(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  if (!open) return null

  return (
    <div className="fixed right-0 top-0 bottom-0 w-96 bg-white border-l border-gray-200 shadow-xl z-40 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-navy-800">
        <div className="flex items-center gap-2">
          <img src="/aida-icon.png" alt="Aida" className="w-5 h-5 object-contain" />
          <span className="text-white font-semibold text-sm">Aida</span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={() => setMessages([])}
              className="text-navy-300 hover:text-white p-1 rounded"
              title="Clear conversation"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} className="text-navy-300 hover:text-white p-1 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <img src="/aida-icon.png" alt="Aida" className="w-12 h-12 object-contain mx-auto mb-3" />
            <p className="text-sm font-medium text-navy-800">How can I help?</p>
            <p className="text-xs text-gray-400 mt-1 max-w-[240px] mx-auto">
              Ask about work orders, assets, maintenance trends, or anything about your property.
            </p>
            <div className="mt-4 space-y-2">
              {[
                'How many overdue work orders do we have?',
                'Summarise this week\'s maintenance activity',
                'Which assets have the most work orders?',
              ].map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); inputRef.current?.focus() }}
                  className="block w-full text-left text-xs text-navy-600 hover:text-navy-800 bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={cn('flex gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-1 p-0.5">
                <img src="/aida-icon.png" alt="Aida" className="w-4 h-4 object-contain" />
              </div>
            )}
            <div
              className={cn(
                'rounded-xl px-3 py-2 max-w-[280px] text-sm',
                msg.role === 'user'
                  ? 'bg-navy-800 text-white'
                  : 'bg-gray-100 text-gray-800'
              )}
            >
              {msg.role === 'assistant' ? (
                <div>
                  {msg.content ? (
                    <>
                      <div className="prose prose-sm prose-gray max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>li]:my-0.5 [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm [&>h1]:font-semibold [&>h2]:font-semibold [&_code]:text-xs [&_code]:bg-gray-200 [&_code]:px-1 [&_code]:rounded [&_strong]:text-navy-800 [&>blockquote]:border-l-2 [&>blockquote]:border-purple-300 [&>blockquote]:pl-2 [&>blockquote]:text-gray-500 [&_table]:text-xs [&_th]:px-2 [&_th]:py-1 [&_td]:px-2 [&_td]:py-1 [&_th]:bg-gray-100 [&_th]:text-left">
                        <Markdown components={{
                          a: ({ children }) => <span className="text-purple-600 font-medium">{children}</span>,
                          code: ({ className, children }) => {
                            const lang = className?.replace('language-', '') ?? ''
                            const text = String(children).trim()
                            if (lang === 'map') {
                              try { const d = JSON.parse(text); if (d.journey?.length) return <InlineMap data={d} /> } catch {}
                            }
                            if (lang === 'risk-matrix') {
                              try { const d = JSON.parse(text); if (d.risks?.length) return <InlineRiskMatrix data={d} /> } catch {}
                            }
                            return <code className={className}>{children}</code>
                          },
                          pre: ({ children }) => <>{children}</>,
                        }}>
                          {msg.content.replace(/\[ACTION:\w+\]/g, '')}
                        </Markdown>
                      </div>
                      <AIActionButtons content={msg.content} />
                    </>
                  ) : streaming && i === messages.length - 1 ? (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  ) : null}
                </div>
              ) : (
                <p className="whitespace-pre-wrap">{msg.content}</p>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 border-t border-gray-100">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Aida..."
            rows={1}
            className="input resize-none flex-1 py-2 text-sm"
            disabled={streaming}
          />
          <button
            type="submit"
            disabled={!input.trim() || streaming}
            className="btn-primary px-3 py-2"
          >
            {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  )
}

// ── AI Action Buttons ───────────────────────────────────────────────────────
const ACTION_CONFIG: Record<string, { label: string; description: string }> = {
  assign_work_orders: { label: 'Auto-Assign Work Orders', description: 'Assign unassigned WOs to available staff based on skills and workload' },
  schedule_ppm: { label: 'Generate PPM Work Orders', description: 'Create work orders for PPM schedules due within 7 days' },
}

function AIActionButtons({ content }: { content: string }) {
  const { currentPropertyId } = useAuthStore()
  const [executing, setExecuting] = useState<string | null>(null)
  const [results, setResults] = useState<Record<string, any>>({})

  const actions = [...content.matchAll(/\[ACTION:(\w+)\]/g)].map(m => m[1])
  if (actions.length === 0) return null

  async function executeAction(action: string) {
    setExecuting(action)
    try {
      const token = JSON.parse(localStorage.getItem('estatekey-auth') ?? '{}')?.state?.accessToken
      let endpoint = ''
      let body: any = { propertyId: currentPropertyId }

      if (action === 'assign_work_orders') endpoint = '/api/v1/ai/actions/assign-work-orders'
      else if (action === 'schedule_ppm') endpoint = '/api/v1/ai/actions/schedule-ppm'
      else return

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      setResults(prev => ({ ...prev, [action]: data }))
    } catch {
      setResults(prev => ({ ...prev, [action]: { error: 'Action failed' } }))
    } finally {
      setExecuting(null)
    }
  }

  return (
    <div className="mt-2 space-y-2">
      {actions.map(action => {
        const cfg = ACTION_CONFIG[action]
        if (!cfg) return null
        const result = results[action]

        return (
          <div key={action}>
            {!result ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); executeAction(action) }}
                disabled={executing === action}
                className="w-full flex items-center gap-2 px-3 py-2 bg-navy-800 text-white text-xs font-medium rounded-lg hover:bg-navy-700 transition-colors disabled:opacity-50"
              >
                {executing === action ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Play className="w-3.5 h-3.5" />
                )}
                {executing === action ? 'Executing...' : cfg.label}
              </button>
            ) : result.error ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 text-xs rounded-lg">
                <AlertTriangle className="w-3.5 h-3.5" />
                {result.error}
              </div>
            ) : (
              <div className="px-3 py-2 bg-green-50 text-green-800 text-xs rounded-lg">
                <div className="flex items-center gap-2 font-medium mb-1">
                  <CheckCircle className="w-3.5 h-3.5" />
                  {result.message}
                </div>
                {result.results?.slice(0, 5).map((r: any, i: number) => (
                  <p key={i} className="text-green-700 ml-5">
                    {r.woNumber}: {r.title} → {r.assignedTo ?? 'Created'}
                  </p>
                ))}
                {result.results?.length > 5 && (
                  <p className="text-green-600 ml-5">+{result.results.length - 5} more</p>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
